import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import * as Sentry from '@sentry/react'
import { AuthProvider } from '@/context/AuthContext'
import { OrgProvider } from '@/context/OrgContext'
import { ThemeProvider } from '@/context/ThemeContext'
import { TourProvider } from '@/context/TourContext'
import { ViewModeProvider } from '@/context/ViewModeContext'
import { initSentry } from '@/lib/sentry'
import './index.css'
import App from './App.tsx'

initSentry()

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
})

// Register service worker for PWA
if ('serviceWorker' in navigator) {
  navigator.serviceWorker.register('/sw.js')
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <Sentry.ErrorBoundary
      fallback={({ error }) => (
        <div className="min-h-screen flex flex-col items-center justify-center px-6 text-center bg-background text-foreground">
          <p className="text-sm font-semibold uppercase tracking-wider text-muted-foreground mb-2">
            Something went wrong
          </p>
          <p className="text-xs text-muted-foreground max-w-sm leading-relaxed mb-6">
            {error instanceof Error ? error.message : 'Unknown error'}
          </p>
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="text-sm text-primary hover:underline"
          >
            Reload the app
          </button>
        </div>
      )}
    >
      <ThemeProvider>
        <QueryClientProvider client={queryClient}>
          <AuthProvider>
            <OrgProvider>
              <TourProvider>
                <ViewModeProvider>
                  <App />
                </ViewModeProvider>
              </TourProvider>
            </OrgProvider>
          </AuthProvider>
        </QueryClientProvider>
      </ThemeProvider>
    </Sentry.ErrorBoundary>
  </StrictMode>,
)
